/**
 * MentionAutocomplete -- floating dropdown for @ entity search results
 * Positioned at the caret using anchorRect coordinates
 */

import { useRef, useEffect } from 'react'
import { FileText, Tag, User, Shield } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { MentionEntity, MentionEntityType } from '@/types/rich-input'
import { ENTITY_TYPE_LABELS } from '@/types/rich-input'

const ICON_MAP: Record<MentionEntityType, React.ElementType> = {
  policy: FileText,
  attribute: Tag,
  subject: User,
  classification: Shield,
}

interface MentionAutocompleteProps {
  readonly results: ReadonlyMap<MentionEntityType, readonly MentionEntity[]>
  readonly flatResults: readonly MentionEntity[]
  readonly isLoading: boolean
  readonly isEmpty: boolean
  readonly query: string
  readonly selectedIndex: number
  readonly anchorRect: { top: number; left: number } | null
  readonly onSelect: (entity: MentionEntity) => void
}

export function MentionAutocomplete({
  results,
  flatResults,
  isLoading,
  isEmpty,
  query,
  selectedIndex,
  anchorRect,
  onSelect,
}: MentionAutocompleteProps) {
  const listRef = useRef<HTMLDivElement>(null)

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selected = listRef.current.querySelector(
        '[data-selected="true"]'
      ) as HTMLElement | null
      selected?.scrollIntoView({ block: 'nearest' })
    }
  }, [selectedIndex])

  if (!anchorRect) return null

  // Track a flat index across all groups to match selectedIndex
  let flatIndex = 0

  return (
    <div
      className="absolute z-50 w-80 rounded-lg border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 duration-150"
      style={{
        top: anchorRect.top,
        left: anchorRect.left,
      }}
      role="listbox"
      aria-label="Mention suggestions"
    >
      <ScrollArea className="max-h-80">
        <div ref={listRef} className="p-1">
          {isLoading && flatResults.length === 0 && (
            <div className="space-y-2 p-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 flex-1 rounded" />
                </div>
              ))}
            </div>
          )}

          {isEmpty && (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              No results for &ldquo;@{query}&rdquo;
            </div>
          )}

          {Array.from(results.entries()).map(([entityType, entities]) => (
            <div key={entityType}>
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                {ENTITY_TYPE_LABELS[entityType]}
              </div>
              {entities.map((entity) => {
                const currentIndex = flatIndex
                flatIndex += 1
                const isSelected = currentIndex === selectedIndex
                const Icon = ICON_MAP[entity.entityType]

                return (
                  <button
                    key={entity.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-selected={isSelected}
                    id={`mention-option-${currentIndex}`}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer transition-colors',
                      isSelected
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-accent/50'
                    )}
                    onClick={() => onSelect(entity)}
                    onMouseDown={(e) => e.preventDefault()} // prevent blur
                  >
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="flex-1 min-w-0 text-left">
                      <span className="font-medium truncate block">
                        {entity.displayName}
                      </span>
                      {entity.description && (
                        <span className="text-xs text-muted-foreground truncate block">
                          {entity.description}
                        </span>
                      )}
                    </div>
                    <Badge variant="outline" className="shrink-0 text-[10px]">
                      {ENTITY_TYPE_LABELS[entity.entityType]}
                    </Badge>
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}
